import {
  ArrowUpRight,
  CircleCheckBig,
  FolderClock,
  Route,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useEffect } from 'react';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../Pagination';
import {
  EmptyState,
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';
import {
  extractQueueDebugProgress,
  extractQueueOutput,
  extractQueuePayload,
  extractQueueRoute,
  hasDisplayValue,
  normalizeProgress,
  summarizeText,
} from '../../lib/appHelpers';

function HistoryView({
  userQueues,
  queuesStatus,
  deletingQueueId,
  onOpenQueue,
  onDeleteQueue,
  onNewUpload,
}) {
  const queuesPagination = usePagination(userQueues, 10);
  const resetQueuesPage = queuesPagination.goToFirstPage;

  useEffect(() => {
    resetQueuesPage();
  }, [resetQueuesPage, userQueues]);

  const completedCount = userQueues.filter((queue) => queue?.status === 'completed').length;
  const processingCount = userQueues.filter(
    (queue) => queue?.status && queue.status !== 'completed' && queue.status !== 'failed'
  ).length;
  const outputCount = userQueues.filter((queue) => Boolean(extractQueueOutput(queue))).length;

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <PageHero
        eyebrow="Queue History"
        title="Activity history"
        lede="Review uploaded queue items, reopen any result, and remove stale entries from the workspace."
        action={
          <button
            className="inline-flex items-center gap-2 rounded-2xl bg-app-accent px-5 py-4 text-sm font-semibold text-white shadow-panel transition hover:bg-app-accent-strong"
            type="button"
            onClick={onNewUpload}
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            <span>New upload</span>
          </button>
        }
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <MetricCard label="Completed" value={completedCount} icon={CircleCheckBig} accentClassName="text-emerald-700" />
        <MetricCard label="Processing" value={processingCount} icon={FolderClock} accentClassName="text-blue-700" />
        <MetricCard label="Output ready" value={outputCount} icon={Sparkles} accentClassName="text-app-ink" />
      </div>

      <div className="mt-6">
        <SectionCard>
          <SectionTitle
            title="Queue items"
            copy="Each card shows request context, processing route, readiness, and actions."
            action={<StatusPill tone={queuesStatus.type === 'warning' ? 'warning' : queuesStatus.type}>{queuesStatus.message}</StatusPill>}
          />

          {queuesPagination.totalItems ? (
            <div className="mt-5">
              <div className="grid gap-4 md:grid-cols-2">
                {queuesPagination.paginatedItems.map((queue) => {
                  const progress = normalizeProgress(extractQueueDebugProgress(queue));
                  const hasOutput = Boolean(extractQueueOutput(queue));

                  return (
                    <article
                      key={queue._id}
                      className="rounded-[24px] border border-app-border/80 bg-app-surface/95 p-5 shadow-subtle"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                            Queue item
                          </p>
                          <p className="mt-2 break-all text-base font-semibold text-app-ink">
                            {queue._id || 'Unknown queue'}
                          </p>
                        </div>
                        <StatusPill tone={hasOutput ? 'success' : queue?.status === 'failed' ? 'error' : 'accent'}>
                          {queue.status || 'Unknown'}
                        </StatusPill>
                      </div>

                      <div className="mt-4 space-y-2 text-sm leading-6 text-app-muted">
                        {hasDisplayValue(extractQueuePayload(queue)?.message) ? (
                          <p className="m-0">
                            <span className="font-semibold text-app-ink">Request:</span>{' '}
                            {summarizeText(extractQueuePayload(queue)?.message)}
                          </p>
                        ) : null}
                        {hasDisplayValue(extractQueueRoute(queue)) ? (
                          <p className="m-0">
                            <span className="font-semibold text-app-ink">Route:</span> {extractQueueRoute(queue)}
                          </p>
                        ) : null}
                        <p className="m-0">
                          <span className="font-semibold text-app-ink">Output:</span>{' '}
                          {hasOutput ? 'Available' : 'Not ready'}
                        </p>
                        {progress?.currentStage ? (
                          <p className="m-0">
                            <span className="font-semibold text-app-ink">Progress:</span> {progress.currentStage}
                          </p>
                        ) : null}
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2">
                        <button
                          className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-3 py-2 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
                          type="button"
                          onClick={() => onOpenQueue(queue)}
                        >
                          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                          <span>Open result</span>
                        </button>
                        <button
                          className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
                          type="button"
                          onClick={() => onDeleteQueue(queue._id)}
                          disabled={deletingQueueId === queue._id}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                          <span>{deletingQueueId === queue._id ? 'Deleting...' : 'Delete'}</span>
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              <Pagination
                currentPage={queuesPagination.currentPage}
                totalPages={queuesPagination.totalPages}
                hasNextPage={queuesPagination.hasNextPage}
                hasPreviousPage={queuesPagination.hasPreviousPage}
                onGoToPage={queuesPagination.goToPage}
                onGoToNext={queuesPagination.goToNextPage}
                onGoToPrevious={queuesPagination.goToPreviousPage}
                onGoToFirst={queuesPagination.goToFirstPage}
                onGoToLast={queuesPagination.goToLastPage}
                startIndex={queuesPagination.startIndex}
                endIndex={queuesPagination.endIndex}
                totalItems={queuesPagination.totalItems}
              />
            </div>
          ) : (
            <div className="mt-5">
              <EmptyState
                title="No queue items yet."
                copy="Create the first upload to start building queue history."
                action={
                  <button
                    className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
                    type="button"
                    onClick={onNewUpload}
                  >
                    <Route className="h-4 w-4" aria-hidden="true" />
                    <span>Open upload screen</span>
                  </button>
                }
              />
            </div>
          )}
        </SectionCard>
      </div>
    </section>
  );
}

export default HistoryView;
